<template>
    <div class="cart-summary2" :class="{ 'cart-summary--open' : isMobileOpen }">
        <div class="cart-summary__header">
            <h2>{{ $t('pages.checkout.summary_section_title') }}</h2>
        </div>

        <div class="cart-summary__body">
            <div class="cart-summary__row" v-if="containsDigitalItems">
                <div>{{ $t('pages.checkout.summary_digital_items_total') }}
                  <Icon name="infoCircle" width="18" class="cursor-pointer" vertical-align="top" v-b-tooltip.hover
                        :title="$t('pages.checkins_detail.price_list_desc1')"/>
                  </div>
                <div>{{ getDigitalItemsTotalOriginalFormatted }}</div>
            </div>

            <!-- PRINT -->
            <div class="cart-summary__row" v-if="containsPrintItems">
                <div>{{ $t('pages.checkout.summary_print_items_total') }}</div>
                <div>{{ getPrintItemsTotalFormatted }}</div>
            </div>

            <div class="cart-summary__row cart-summary__row--expanded-header" v-if="containsPrintItems">
                <div>{{ $t('pages.checkout.summary_shipping_costs') }}</div>
                <div>{{ getShippingPriceFormatted }}</div>
            </div>

            <div class="cart-summary__row cart-summary__row--expanded-body" v-if="containsPrintItems">
                <ModalCheckoutShippingAddress ref="checkoutShippingSection"/>

                <div class="w-100" v-if="hasShippingAddress">
                    <div class="cart-summary__row--item d-lg-flex align-items-center justify-content-between w-100">
                        <div class="w-100">
                            <div class="small mb-1 text-uppercase d-flex align-items-center justify-content-between">
                                <div class="font-weight-bold">{{ $t('pages.checkout.summary_shipping_address') }}</div>
                                <div>
                                    <Icon name="edit" width="20" class=" float-right cursor-pointer" @click.prevent="$refs.checkoutShippingSection.showz()"
                                         v-b-tooltip.hover :title="$t('pages.checkout.action_edit_shipping_address')"/>
                                </div>
                            </div>
                            <address class="font-weight-normal small m-0 mt-3">
                                <div class="h6 m-0 _font-weight-bold">
                                    {{ getShippingAddressFields.shipping_address_first_name }}
                                    {{ getShippingAddressFields.shipping_address_last_name }}
                                </div>
                                <div>
                                    {{ getShippingAddressFields.shipping_address_street }},
                                    {{ getShippingAddressFields.shipping_address_number }}
                                </div>
                                <div>
                                    {{ getShippingAddressFields.shipping_address_zip_code }}
                                    {{ getShippingAddressFields.shipping_address_city }},
                                    {{ getShippingAddressFields.shipping_address_area }}
                                </div>
                                <div class="text-secondary">{{ getShippingAddressFields.shipping_address_phone }}</div>
                                <div class="text-secondary">{{ getShippingAddressFields.shipping_address_email }}</div>
                            </address>
                        </div>
                    </div>
                </div>
                <div class="d-flex align-items-center justify-content-between w-100" v-else>
                    <div class="small text-uppercase">{{ $t('pages.checkout.summary_shipping_address') }}</div>
                    <a class="bg-dark py-2 px-3 small rounded font-weight-bold text-uppercase d-inline-block" href=""
                       @click.prevent="$refs.checkoutShippingSection.showz()">
                        <span class="small">{{ $t('pages.checkout.action_add_shipping_address') }}</span>
                    </a>
                </div>
            </div>
            <!-- END PRINT -->

            <!-- DISCOUNT -->
            <div class="cart-summary__row" v-if="getDiscountTotal">
                <div >{{ $t('pages.checkout.summary_discount_total') }}</div>
                <div>-{{ getDiscountTotalFormatted }}</div>
            </div>

            <!-- FEE -->
            <div class="cart-summary__row cart-summary__row--light cart-summary__row--expanded-header" v-if="getHandlingFeeFormatted">
                <div>{{ $t('pages.checkout.summary_handling_fee') }}</div>
                <div class="text-nowrap">{{ getHandlingFeeFormatted }}</div>
            </div>

            <!-- TOTAL -->
            <div class="cart-summary__row cart-summary__row--expanded-header">
                <div >{{ $t('pages.checkout.summary_grand_total') }}</div>
                <div>{{ getGrandTotalFormatted }}</div>
            </div>

            <div class="cart-summary__row cart-summary__row--expanded-body _rounded d-block d-inline-lg-block _text-center"
                 v-if="!isZeroAmountOrder">
                <div class="text-uppercase">{{ $t('pages.checkout.summary_payment_method') }}</div>
                <div class="mt-3">
                    <b-form-group class="m-0 mt-2 m-0 mt-2">
                          <label class="grouper-mp-argentina" v-if="showMercadoPagoGroupLabels">{{ $t('pages.checkout.payment_method_argentina') }}</label>
                          <b-form-radio v-model="paymentMethodSelected" name="payment-method"
                                      :value="PAYMENT_METHOD_MERCADO_PAGO"
                                      class="payment-method-option payment-method-option--mercado-pago"
                                      v-if="allowMercadoPagoPayment">
                            {{ $t('pages.checkout.payment_method_mercado_pago') }}
                        </b-form-radio>
                        <label class="grouper-mp-argentina" v-if="showMercadoPagoGroupLabels">{{ $t('pages.checkout.payment_method_other_countries') }}</label>
                        <b-form-radio v-model="paymentMethodSelected" name="payment-method" :value="PAYMENT_METHOD_CARD"
                                      class="payment-method-option payment-method-option--cc"
                                      v-if="allowCreditCardPayment">
                            {{ $t('pages.checkout.payment_method_card') }}
                        </b-form-radio>
                        <b-form-radio v-model="paymentMethodSelected" name="payment-method"
                                      :value="PAYMENT_METHOD_IDEAL"
                                      class="payment-method-option payment-method-option--ideal"
                                      v-if="allowIdealPayment">
                            {{ $t('pages.checkout.payment_method_ideal') }}
                        </b-form-radio>
                        <b-form-radio v-model="paymentMethodSelected" name="payment-method"
                                      :value="PAYMENT_METHOD_P24"
                                      class="payment-method-option payment-method-option--p24"
                                      v-if="allowP24Payment">
                            {{ $t('pages.checkout.payment_method_p24') }}
                        </b-form-radio>


                        <b-form-radio v-model="paymentMethodSelected" name="payment-method"
                                      :value="PAYMENT_METHOD_BANK_TRANSFER"
                                      class="payment-method-option payment-method-option--bank"
                                      v-if="allowBankTransferPayment">
                          <Icon name="bank" width="19" class="mx-2" vertical-align="sub" />  {{ $t('pages.checkout.payment_method_bank_transfer') }}
                        </b-form-radio>
                    </b-form-group>
                </div>
            </div>
        </div>

        <div class="cart-summary__console">
            <a href="" class="btn-toggle-body" @click.prevent="isMobileOpen = !isMobileOpen">
                {{ $t('pages.checkout.summary_section_title') }} <img src="@/assets/icon-arrow.svg">
            </a>

            <a class="btn-pay" href="" @click.prevent="payClicked()">
                <template v-if="isZeroAmountOrder">
                    {{ $t('pages.checkout.summary_action_get_for_free') }}
                </template>
                <template v-else>
                    {{ $t('pages.checkout.summary_action_buy_mobile') }}
                    <span class="btn-pay__amount">{{ getGrandTotalFormatted }}</span>
                </template>
            </a>
        </div>
    </div>
</template>


<script>
import {mapGetters} from 'vuex'
import {
    PAYMENT_METHOD_CARD,
    PAYMENT_METHOD_IDEAL,
    PAYMENT_METHOD_BANK_TRANSFER,
    PAYMENT_METHOD_MERCADO_PAGO, PAYMENT_METHOD_P24
} from "@/consts.js"
import ModalCheckoutShippingAddress from '@/components/ModalCheckoutShippingAddress'


export default {
    components: {
        ModalCheckoutShippingAddress
    },

    data() {
        return {
            isMobileOpen: false,
            paymentMethodSelected: null,
        }
    },

    created() {
        this.PAYMENT_METHOD_CARD = PAYMENT_METHOD_CARD
        this.PAYMENT_METHOD_BANK_TRANSFER = PAYMENT_METHOD_BANK_TRANSFER
        this.PAYMENT_METHOD_IDEAL = PAYMENT_METHOD_IDEAL
        this.PAYMENT_METHOD_MERCADO_PAGO = PAYMENT_METHOD_MERCADO_PAGO
        this.PAYMENT_METHOD_P24 = PAYMENT_METHOD_P24

        this.setDefaultPaymentMethod()

        if (this.allowMercadoPagoPayment) {
            this.loadMercadoPagoSdk()
        }
    },

    computed: {
        ...mapGetters('cart', [
            'containsDigitalItems',
            'containsPrintItems',
            'getDigitalItemsTotalOriginalFormatted',
            'getPrintItemsTotalFormatted',
            'hasShippingAddress',
            'getShippingAddressFields',
            'getShippingPriceFormatted',
            'getGrandTotalFormatted',
            'getHandlingFeeFormatted',
            'getDiscountTotal',
            'getDiscountTotalFormatted',
            'getPaymentMethods',
            'getPaymentSettings'
        ]),

        isZeroAmountOrder() {
            return this.$store.getters['cart/getGrandTotal'] === 0 && this.hasCartItems
        },

        hasCartItems() {
            return this.containsDigitalItems || this.containsPrintItems
        },

        allowCreditCardPayment() {
            return this.getPaymentMethods.includes('card')
        },

        allowIdealPayment() {
            return this.getPaymentMethods.includes('ideal')
        },
        allowP24Payment() {
            return this.getPaymentMethods.includes('p24')
        },
        allowMercadoPagoPayment() {
            return this.getPaymentMethods.includes('mercado-pago')
        },
        allowBankTransferPayment() {
            return this.getPaymentMethods.includes('bank_transfer')
        },
        showMercadoPagoGroupLabels() {
            return this.allowMercadoPagoPayment && this.getPaymentMethods.length > 1
        },

    },

    methods: {
        // this method is necessary to preselect the default payment method
        // the default method is got from available payment methods form apis and re-mapped to local consts of vue
        // ideally the values from apis should match local constants...
        setDefaultPaymentMethod() {
            let firstMethodPayment
            let paymentMethod
            if (this.getPaymentMethods.length > 0) {
                firstMethodPayment = this.getPaymentMethods[0]
                if (firstMethodPayment === 'card') paymentMethod = PAYMENT_METHOD_CARD
                if (firstMethodPayment === 'ideal') paymentMethod = PAYMENT_METHOD_IDEAL
                if (firstMethodPayment === 'mercado-pago') paymentMethod = PAYMENT_METHOD_MERCADO_PAGO
                if (firstMethodPayment === 'p24') paymentMethod = PAYMENT_METHOD_P24
                if (firstMethodPayment === 'bank_transfer') paymentMethod = PAYMENT_METHOD_BANK_TRANSFER
            }
            return this.$set(this, 'paymentMethodSelected', paymentMethod)
        },
        payClicked() {
            if (this.containsPrintItems) {
                if (!this.hasShippingAddress) {
                    this.$refs.checkoutShippingSection.showz()
                    return false
                }
            }

            this.$emit('pay-clicked', this.isZeroAmountOrder ? null : this.paymentMethodSelected)
        },
        loadMercadoPagoSdk() {
            const script = document.createElement("script");
            script.src = "https://sdk.mercadopago.com/js/v2"
            script.async = false
            script.defer = false
            document.body.appendChild(script)
        }
    }
}
</script>
